import Incident from "../components/Incidents/Incident";
import { closedIncidents, incidents } from "../endpoints";
import useFetch from "../hooks/useFetch";
import { useState } from "react";

import { useClickFilter } from "../hooks/filtering";
import { useSearchBar } from "../hooks/search";

interface data {
  number: string;
  description: string;
  client: string;
  status: string;
  time: string;
  target: string;
  priority: string;
  call_type: string;
  line: string;
  caller: string;
  operator: string;
  operator_group: string;
}

const IncidentsLayout = () => {
  const [isOpen, setIsOpen] = useState(true);
  const endpoint = isOpen ? incidents : closedIncidents;
  const { data, loading } = useFetch<data>(endpoint);

  const {
    searchEntry,
    setSearchEntry,

    setIsSearchFocused,

    handleFilterClick,
    shouldShowFilters,
    searchRef,
    getFilteredSearchProjects,
  } = useSearchBar(data);

  const {
    numberSort,
    toggleNumberSort,

    descriptionSort,
    toggleDescriptionSort,

    clientSort,
    toggleClientSort,

    getArrow,
    getFilteredData,
  } = useClickFilter();

  // Combine search and click filters
  const getFilteredAndSortedData = () => {
    if (!data) return [];
    
    const searchFiltered = getFilteredSearchProjects();
    
    return getFilteredData(searchFiltered);
  };

    const handleToggleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsOpen(!event.target.checked); // When checked = true, show open incidents (since slider is a toggle)
  };

  return (
    <div className="overflow-hidden w-full">
      {/* Header */}
      <div>
        <div className="flex justify-between">
          {/* Header Text */}
          <div>
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center">
              <h1 className="font-bold text-3xl sm:text-4xl">
                {loading ? (
                  <span className="text-gray-400">Meldingen laden...</span>
                ) : (
                  `Meldingen`
                )}
              </h1>
            </div>
            <p className="mt-4">
              {loading ? (
                <span className="text-gray-400">
                  Een moment geduld, de gegevens worden geladen.
                </span>
              ) : (
                "Hier een overzicht van alle Meldingen"
              )}
            </p>
          </div>
          {/* Dropdowns with filters */}
          <div className="my-auto">
            <label className="inline-flex items-center cursor-pointer">
              <input 
                type="checkbox" 
                onChange={handleToggleChange}
                checked={!isOpen}
                className="sr-only peer"
              />
              <span className="select-none ms-3 text-sm font-medium text-heading">Open</span>
              <div className="relative w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-brand-soft dark:peer-focus:ring-brand-soft rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-brand"></div>
              <span className="select-none ms-3 text-sm font-medium text-heading">Closed</span>
            </label>
          </div>
          <div className="relative flex-1 flex justify-end" ref={searchRef}>
            {/* Search area */}
            <div className="bg-white w-[50%] h-12 rounded-3xl flex justify-center my-auto border border-gray-200">
              <input
                className="h-6 w-[80%] m-auto focus:outline-none"
                onChange={(e) => setSearchEntry(e.target.value)}
                onFocus={() => setIsSearchFocused(true)}
                value={searchEntry}
                placeholder="Zoeken..."
              />
            </div>

            {/* Filters */}
            {shouldShowFilters && (
              <div className="absolute top-full right-0 z-10 bg-white shadow-lg rounded-lg p-4 border border-gray-200 min-w-[50%] mt-2">
                <b className="text-gray-600 text-xs">Filters</b>
                <div
                  className="text-gray-600 text-sm p-2 cursor-pointer rounded-lg hover:bg-gray-100"
                >
                  <b>Geen specefieke filters mogelijk op het moment</b>
                  <div className="flex text-xs">
                    <b>Zoeken is wel mogelijk</b>
                  </div>
                </div>
              </div>
            )}
          </div>
          
        </div>

        {/* Data Table */}
        <div className="p-8 bg-white  rounded-xl mt-6 w-full overflow-auto">
          {data.length > 0 && !loading && (
            <div className="w-full text-gray-600 flex text-sm p-2 border-b-2">
              <div
                className="w-1/12 flex cursor-pointer select-none"
                onClick={toggleNumberSort}
              >
                <b>Nummer</b>
                {getArrow(numberSort) && (
                  <img
                    src={getArrow(numberSort)!}
                    className="h-5 w-auto ml-1"
                    alt=""
                  />
                )}
              </div>
              <div
                className="w-2/12 flex cursor-pointer select-none"
                onClick={toggleDescriptionSort}
              >
                <b>Beschrijving</b>
                {getArrow(descriptionSort) && (
                  <img
                    src={getArrow(descriptionSort)!}
                    className="h-5 w-auto ml-1"
                    alt=""
                  />
                )}
              </div>
              <div
                className="w-2/12 flex cursor-pointer select-none"
                onClick={toggleClientSort}
              >
                <b>Klant</b>
                {getArrow(clientSort) && (
                  <img
                    src={getArrow(clientSort)!}
                    className="h-5 w-auto ml-1"
                    alt=""
                  />
                )}
              </div>
              <b className="w-2/12 text-left">Status</b>
            </div>
          )}
          {loading ? (
            <div className="min-w-full bg-gray-200 p-4 font-semibold text-gray-700 dark:text-gray-300 rounded-xl mt-1 animate-pulse">
              <p>Incidenten laden...</p>
            </div>
          ) : data.length === 0 ? (
            <tr>
              <td
                colSpan={6}
                className="p-4 text-center text-gray-500 text-xl font-semibold"
              >
                Geen open incidenten
              </td>
            </tr>
          ) : (
            getFilteredAndSortedData().map((incident, index) => (
              <Incident
                key={index}
                number={incident.number}
                description={incident.description}
                client={incident.client}
                status={incident.status}
                time={incident.time}
                target={incident.target}
                priority={incident.priority}
                line={incident.line}
                caller={incident.caller}
                operator={incident.operator}
              />
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default IncidentsLayout;
